import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  id: "show-settings",
  class: "width-100 height-100 d-flex flex-fill"
}
const _hoisted_2 = {
  class: "d-flex flex-row width-100 height-100",
  style: {"padding":"8px"}
}
const _hoisted_3 = { class: "background-fb z-index-15" }
const _hoisted_4 = { class: "card border-0 background-fb" }
const _hoisted_5 = { class: "loader-div-40" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "card-header header-label d-flex flex-row justify-content-between align-items-center" }
const _hoisted_8 = { class: "configuration-settings-header" }
const _hoisted_9 = { class: "card-body d-flex flex-column overflow-auto font-size-14" }
const _hoisted_10 = {
  class: "filter-controls",
  style: {"margin-bottom":"10px"}
}
const _hoisted_11 = { class: "custom-tree-container" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  key: 1,
  class: "key-muted"
}
const _hoisted_14 = {
  key: 0,
  style: {"width":"100%"}
}
const _hoisted_15 = {
  key: 0,
  class: "card-footer footer"
}
const _hoisted_16 = { class: "float-right" }

import { computed, onMounted, ref, watch } from "vue";
import {
  ElButton,
  ElCard,
  ElDivider,
  ElInput,
  ElOption,
  ElScrollbar,
  ElSelect,
  ElTree,
} from "element-plus";
import i18n from "@/core/i18n/i18n";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import { organizationRoles } from "@/core/auth/user-role";
import { getUserById } from "@/user/services/user.service";
import { authInfo } from "@/core/auth";
import {
  getConfigurationSettings,
  updateConfiguraitonSettings,
} from "@/settings/services/settings.service";
import { TYPE } from "vue-toastification";
import { showAlert } from "@/core/popup";

interface TreeNode {
  id?: string;
  label: string;
  level?: string | number;
  description?: string;
  deleted?: boolean;
  type?: string;
  defaultValue?: unknown;
  value?: unknown;
  inputValue?: unknown;
  lastSavedValue?: unknown;
  enumerationValues?: string[];
  index?: string;
  children?: TreeNode[];
}

export default /*#__PURE__*/_defineComponent({
  __name: 'Settings',
  setup(__props) {

const { t } = i18n.global;

const defaultProps = {
  children: "children",
  label: "label",
};
const responseLoading = ref(false);
const savingLoading = ref(false);
const organizations = ref<any>(null);
const isCheckBoxVisible = ref(false);
const sortedOrganizationRoles = computed(() =>
  // OT-4895: drop orphaned org-roles (deleted org -> organization == null) before
  // sorting, and copy the array so we don't mutate the source in place.
  organizationRoles.value
    .filter((role) => role && role.organization)
    .slice()
    .sort((a, b) => a.organization.name.localeCompare(b.organization.name))
);
const treeData = ref<TreeNode[]>([]);
const selectedOption = ref(null);
const isTenantAdmin = ref(true);
const treeRef = ref();
const filterText = ref("");
const baseColor = [170, 170, 170];
const treeSource = ref("");

onMounted(async () => {
  const userOrganizations = await getUserById(authInfo.value.userId);
  organizations.value = userOrganizations;
  isCheckBoxVisible.value = organizations.value?.tenantAdmin === true;
  if (isCheckBoxVisible.value) {
    // isTenantAdmin.value = true;
    await loadConfigSettings();
  }
});

const handleCheckboxChange = async () => {
  if (isTenantAdmin.value) {
    selectedOption.value = null;
    await loadConfigSettings();
  } else {
    treeData.value = [];
  }
};

const handleSelectChange = async () => {
  if (!isTenantAdmin.value && !selectedOption.value) {
    treeData.value = [];
  } else if (selectedOption.value) {
    await loadConfigSettings(selectedOption.value);
  }
};

const loadConfigSettings = async (organizationId?: string) => {
  responseLoading.value = true;
  let configData;
  try {
    if (isTenantAdmin.value) {
      configData = await getConfigurationSettings(
        "TENANT",
        authInfo.value.tenantId
      );
    } else if (organizationId) {
      configData = await getConfigurationSettings(
        "ORGANIZATION",
        organizationId
      );
    }

    if (configData && configData.length > 0) {
      treeSource.value = "config";
      treeData.value = mapConfigSettingsToTree(configData);
    } else {
      treeSource.value = "config";
      treeData.value = [];
    }
  } catch (error) {
    console.error("Error fetching configuration settings:", error);
  } finally {
    responseLoading.value = false;
  }
};

watch(filterText, (val) => {
  const tree = treeRef.value;
  if (tree) tree.filter(val);
});

const filterNode = (value: string, data: any) => {
  if (!value) return true;
  const v = value.toLowerCase();
  const keyMatch = (data.label ?? "").toLowerCase().includes(v);
  const descMatch = (data.description ?? "").toLowerCase().includes(v);
  const valueMatch = String(data.inputValue ?? "")
    .toLowerCase()
    .includes(v);
  return keyMatch || descMatch || valueMatch;
};

const mapTreeToPayload = (node: any) => ({
  deleted: node.deleted !== undefined ? node.deleted : false,
  key: node.label,
  level: node.level,
  description: node.description || node.label,
  type: node.type,
  value: node.inputValue || node.value,
  defaultValue: node.defaultValue,
  children: node.children ? node.children.map(mapTreeToPayload) : [],
  ownerId: node.id,
  ...(node.enumerationValues
    ? { enumerationValues: node.enumerationValues }
    : {}),
});

function collectChangedLeaves(nodes: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = [];
  function recurse(arr: TreeNode[]) {
    arr.forEach((node) => {
      if (node.children?.length) {
        recurse(node.children);
      } else {
        // eslint-disable-next-line
        if (node.inputValue !== node.lastSavedValue) {
          result.push(node);
        }
      }
    });
  }
  recurse(nodes);
  return result;
}

function mapNodeToPayload(node: TreeNode) {
  return {
    deleted: node.deleted ?? false,
    key: node.label ?? "",
    level: node.level ?? "",
    description: node.description ?? "",
    type: node.type ?? "",
    value: node.inputValue ?? node.value ?? "",
    defaultValue: node.defaultValue ?? "",
    children: [],
    ownerId: node.id ?? "",
    enumerationValues: node.enumerationValues ?? [],
  };
}

async function saveChanges() {
  savingLoading.value = true;
  try {
    if (treeSource.value === "exec") {
      const root = treeData.value[0];

      if (!root.id) {
        root.id = isTenantAdmin.value
          ? authInfo.value.tenantId
          : (selectedOption.value as unknown as string);
      }

      const payload = {
        deleted: root.deleted ?? false,
        key: "Root",
        level: isTenantAdmin.value ? "TENANT" : "ORGANIZATION",
        description: root.description ?? "Root",
        type: "STRING",
        value: root.inputValue ?? root.defaultValue ?? "",
        ownerId: root.id,
        enumerationValues: root.enumerationValues ?? [],
        children: (root.children ?? []).map(mapTreeToPayload),
      };

      const res = await updateConfiguraitonSettings(payload);
      if (res.code === 200 || res.code === 201) {
        showAlert(TYPE.SUCCESS, t("ConfigurationSettings.UpdatedSuccessfully"));
      } else {
        showAlert(TYPE.ERROR, t("ConfigurationSettings.UpdatedFailed"));
      }
    } else {
      const changed = collectChangedLeaves(treeData.value);
      if (changed.length === 0) {
        showAlert(TYPE.INFO, t("ConfigurationSettings.NoChanges"));
        return;
      }
      const payloads = changed.map(mapNodeToPayload);
      await Promise.all(payloads.map((p) => updateConfiguraitonSettings(p)));
      showAlert(TYPE.SUCCESS, t("ConfigurationSettings.UpdatedSuccessfully"));
    }

    if (selectedOption.value) {
      await loadConfigSettings(selectedOption.value as unknown as string);
    } else {
      await loadConfigSettings();
    }
  } catch (err) {
    console.error(err);
    showAlert(TYPE.ERROR, t("ConfigurationSettings.UpdatedFailed"));
  } finally {
    savingLoading.value = false;
  }
}
const getNodeStyle = (data: TreeNode) => {
  if (!data.children || data.children.length === 0) {
    return {
      background: "#fbfbfb",
    };
  }

  const level = data.index ? data.index.split(".").length - 1 : 0;
  const colorAdjustment = level * 20;
  const adjustedColor = baseColor.map((c) =>
    Math.min(c + colorAdjustment, 255)
  );
  return {
    background: `rgb(${adjustedColor[0]}, ${adjustedColor[1]}, ${adjustedColor[2]})`,
  };
};
const cardStyle = computed(() =>
  treeData.value.length === 0
    ? { height: "30%", transition: "height 0.3s ease" }
    : { height: "100%", transition: "height 0.3s ease" }
);
const handleExpand = (node: any) => {
  if (node.expanded) {
    node.collapse();
  } else {
    node.expand();
  }
};
const mapConfigSettingsToTree = (data: any[], parentIndex = ""): TreeNode[] =>
  data
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((item, index) => {
      const currentIndex = `${parentIndex}${index + 1}.`;
      return {
        id: item.ownerId,
        label: item.key,
        level: item.level,
        description: item.description,
        defaultValue: item.defaultValue,
        deleted: item.deleted,
        type: item.type,
        inputValue: item.value || "",
        lastSavedValue: item.value || "",
        index: currentIndex,
        children: item.children
          ? mapConfigSettingsToTree(item.children, currentIndex)
          : [],
        enumerationValues: item.enumerationValues || [],
      };
    });

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(ElCard), {
        class: "card border-0",
        style: _normalizeStyle([
          cardStyle.value,
          { boxShadow: 'unset !important', background: '#fbfbfb' },
        ]),
        "body-style": { padding: '0 8px 8px 8px', background: '#fbfbfb' }
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _withDirectives(_createElementVNode("div", _hoisted_5, [
                _createElementVNode("img", {
                  src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
                  alt: "Loading...",
                  class: "loader-logo-40"
                }, null, 8, _hoisted_6)
              ], 512), [
                [_vShow, responseLoading.value || savingLoading.value]
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("ConfigurationSettings.Settings")), 1)
              ]),
              _createVNode(_unref(ElDivider), { style: {"background-color":"#bdddfa","margin":"5px 0 0 0"} }),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  (treeData.value.length)
                    ? (_openBlock(), _createBlock(_unref(ElInput), {
                        key: 0,
                        modelValue: filterText.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filterText).value = $event)),
                        size: "small",
                        placeholder: _unref(t)('Common.Filter'),
                        style: {"margin-top":"10px"}
                      }, null, 8, ["modelValue", "placeholder"]))
                    : _createCommentVNode("", true)
                ]),
                _createVNode(_unref(ElScrollbar), { "max-height": '60vh' }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_11, [
                      _createVNode(_unref(ElTree), {
                        ref_key: "treeRef",
                        ref: treeRef,
                        data: treeData.value,
                        props: defaultProps,
                        "default-expand-all": "",
                        "filter-node-method": filterNode
                      }, {
                        default: _withCtx(({ node, data }) => [
                          _createElementVNode("div", {
                            class: "custom-tree-node",
                            style: _normalizeStyle(getNodeStyle(data))
                          }, [
                            _createElementVNode("span", null, [
                              (data.children && data.children.length > 0)
                                ? (_openBlock(), _createElementBlock("i", {
                                    key: 0,
                                    class: _normalizeClass(["settings-expand-icon", { expanded: node.expanded }]),
                                    style: {"margin-right":"5px"},
                                    onClick: _withModifiers(($event: any) => (handleExpand(node)), ["stop"])
                                  }, [
                                    (node.expanded)
                                      ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                                          key: 0,
                                          icon: "minus",
                                          size: "sm"
                                        }))
                                      : (_openBlock(), _createBlock(_component_font_awesome_icon, {
                                          key: 1,
                                          icon: "plus",
                                          size: "sm"
                                        }))
                                  ], 10, _hoisted_12))
                                : _createCommentVNode("", true),
                              _createTextVNode(" " + _toDisplayString(data.description || data.label) + " ", 1),
                              (data?.label)
                                ? (_openBlock(), _createElementBlock("small", _hoisted_13, " (" + _toDisplayString(data.label) + ")", 1))
                                : _createCommentVNode("", true)
                            ]),
                            (!data.children || data.children.length === 0)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                  (
                              data.enumerationValues &&
                              data.enumerationValues.length > 0
                            )
                                    ? (_openBlock(), _createBlock(_unref(ElSelect), {
                                        key: 0,
                                        modelValue: data.inputValue,
                                        "onUpdate:modelValue": ($event: any) => ((data.inputValue) = $event),
                                        size: "small",
                                        placeholder: "Select value",
                                        id: 'select-' + data.label
                                      }, {
                                        default: _withCtx(() => [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.enumerationValues, (option) => {
                                            return (_openBlock(), _createBlock(_unref(ElOption), {
                                              key: option,
                                              label: option,
                                              value: option
                                            }, null, 8, ["label", "value"]))
                                          }), 128))
                                        ]),
                                        _: 2
                                      }, 1032, ["modelValue", "onUpdate:modelValue", "id"]))
                                    : (_openBlock(), _createBlock(_unref(ElInput), {
                                        key: 1,
                                        modelValue: data.inputValue,
                                        "onUpdate:modelValue": ($event: any) => ((data.inputValue) = $event),
                                        size: "small",
                                        placeholder: "Enter a value",
                                        "data-index": data.index
                                      }, null, 8, ["modelValue", "onUpdate:modelValue", "data-index"]))
                                ]))
                              : _createCommentVNode("", true)
                          ], 4)
                        ]),
                        _: 1
                      }, 8, ["data"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              (treeData.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(_unref(ElButton), {
                        class: "btn-oneTeg2",
                        type: "primary",
                        onClick: saveChanges
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("Common.Save")), 1)
                        ]),
                        _: 1
                      })
                    ])
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _: 1
      }, 8, ["style"])
    ])
  ]))
}
}

})